/**
 * AuthContext - Azure Entra ID Authentication Context
 *
 * Provides authentication state management and methods throughout the application.
 * Uses MSAL (Microsoft Authentication Library) for Azure AD integration.
 *
 * Features:
 * - Authentication state management
 * - Login/logout functionality
 * - Session timeout management (10 hours / 600 minutes)
 * - Token refresh handling
 * - User profile information
 * - Authentication status tracking
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { notifications } from '@mantine/notifications';
import {
  loginRequest,
  silentRequest,
  apiTokenRequest,
  sessionConfig,
  authErrorMessages
} from '../config/authConfig';

// Create the Auth Context
const AuthContext = createContext(null);

/**
 * Hook to use the AuthContext
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider component that wraps the application and provides authentication state
 */
export const AuthProvider = ({ children }) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});

  // Local state
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [sessionTimeoutWarning, setSessionTimeoutWarning] = useState(false);

  // Session management refs
  const sessionTimeoutRef = useRef(null);
  const sessionWarningRef = useRef(null);
  const sessionCheckRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  /**
   * Update last activity timestamp
   */
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSessionTimeoutWarning(false);
  }, []);

  /**
   * Clear all session timers
   */
  const clearSessionTimers = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (sessionWarningRef.current) {
      clearTimeout(sessionWarningRef.current);
      sessionWarningRef.current = null;
    }
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
      sessionCheckRef.current = null;
    }
  }, []);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      clearSessionTimers();
      setSessionTimeoutWarning(false);

      // Clear local storage and session storage
      localStorage.clear();
      sessionStorage.clear();

      await instance.logoutRedirect({
        account: account,
      });
    } catch (error) {
      console.error('Logout error:', error);
      notifications.show({
        title: 'Logout Error',
        message: 'An error occurred during logout. Please close your browser.',
        color: 'red',
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [instance, account, clearSessionTimers]);

  /**
   * Handle session timeout
   */
  const handleSessionTimeout = useCallback(async () => {
    try {
      setSessionTimeoutWarning(false);
      notifications.show({
        title: 'Session Expired',
        message: 'Your session has expired. Please sign in again.',
        color: 'red',
        autoClose: 5000,
      });
      await logout();
    } catch (error) {
      console.error('Error during session timeout:', error);
    }
  }, [logout]);

  /**
   * Show session timeout warning
   */
  const showSessionWarning = useCallback(() => {
    setSessionTimeoutWarning(true);
    notifications.show({
      title: 'Session Expiring Soon',
      message: `Your session will expire in ${sessionConfig.warningMinutes} minutes. Please save your work.`,
      color: 'yellow',
      autoClose: 10000,
    });
  }, []);

  /**
   * Set up session management timers
   */
  const setupSessionManagement = useCallback(() => {
    clearSessionTimers();

    if (!isAuthenticated) return;

    // Set warning timer
    const warningTime = (sessionConfig.timeoutMinutes - sessionConfig.warningMinutes) * 60 * 1000;
    sessionWarningRef.current = setTimeout(showSessionWarning, warningTime);

    // Set timeout timer
    const timeoutTime = sessionConfig.timeoutMinutes * 60 * 1000;
    sessionTimeoutRef.current = setTimeout(handleSessionTimeout, timeoutTime);

    // Set up periodic session check
    sessionCheckRef.current = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      const timeoutMillis = sessionConfig.timeoutMinutes * 60 * 1000;

      if (timeSinceActivity >= timeoutMillis) {
        handleSessionTimeout();
      }
    }, sessionConfig.checkIntervalSeconds * 1000);

  }, [isAuthenticated, clearSessionTimers, showSessionWarning, handleSessionTimeout]);

  /**
   * Login function
   */
  const login = useCallback(async () => {
    try {
      setAuthError(null);
      setIsLoading(true);

      await instance.loginRedirect(loginRequest);
      updateActivity();
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(authErrorMessages.loginFailed);
      notifications.show({
        title: 'Login Failed',
        message: authErrorMessages.loginFailed,
        color: 'red',
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [instance, updateActivity]);

  /**
   * Get access token silently
   */
  const getAccessToken = useCallback(async () => {
    try {
      if (!account) {
        throw new Error('No account available');
      }

      const response = await instance.acquireTokenSilent({
        ...silentRequest,
        account: account,
      });

      updateActivity();
      return response.accessToken;
    } catch (error) {
      console.error('Token acquisition error:', error);
      setAuthError(authErrorMessages.tokenExpired);
      throw error;
    }
  }, [instance, account, updateActivity]);

  /**
   * Get API access token for calling our API Gateway endpoints.
   * Uses the custom API scope so the token audience matches our app registration.
   */
  const getApiToken = useCallback(async () => {
    try {
      if (!account) {
        throw new Error('No account available');
      }

      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account: account,
      });

      updateActivity();
      return response.accessToken;
    } catch (error) {
      console.error('API token acquisition error:', error);
      setAuthError(authErrorMessages.tokenExpired);
      throw error;
    }
  }, [instance, account, updateActivity]);

  /**
   * Refresh authentication status
   */
  const refreshAuth = useCallback(async () => {
    try {
      if (account) {
        await getAccessToken();
      }
    } catch (error) {
      console.error('Auth refresh error:', error);
      await logout();
    }
  }, [account, getAccessToken, logout]);

  /**
   * Get user information
   */
  const getUserInfo = useCallback(() => {
    if (!account) return null;

    return {
      id: account.localAccountId,
      username: account.username,
      name: account.name || account.username,
      email: account.username,
      tenantId: account.tenantId,
    };
  }, [account]);

  /**
   * Extend session (reset timeout)
   */
  const extendSession = useCallback(() => {
    updateActivity();
    setupSessionManagement();
    setSessionTimeoutWarning(false);
    notifications.show({
      title: 'Session Extended',
      message: 'Your session has been extended.',
      color: 'green',
      autoClose: 3000,
    });
  }, [updateActivity, setupSessionManagement]);

  // Effect to handle authentication state changes
  useEffect(() => {
    const handleAuthStateChange = () => {
      if (inProgress === InteractionStatus.None) {
        setIsLoading(false);

        if (isAuthenticated) {
          updateActivity();
          setupSessionManagement();
          setAuthError(null);
        } else {
          clearSessionTimers();
        }
      }
    };

    handleAuthStateChange();
  }, [isAuthenticated, inProgress, updateActivity, setupSessionManagement, clearSessionTimers]);

  // Effect to set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const activityHandler = () => {
      updateActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, activityHandler, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, activityHandler, true);
      });
    };
  }, [isAuthenticated, updateActivity]);


  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSessionTimers();
    };
  }, [clearSessionTimers]);

  const contextValue = {
    // Authentication state
    isAuthenticated,
    isLoading: isLoading || inProgress !== InteractionStatus.None,
    authError,
    account,

    // User information
    user: getUserInfo(),

    // Authentication methods
    login,
    logout,
    refreshAuth,
    getAccessToken,
    getApiToken,

    // Session management
    sessionTimeoutWarning,
    extendSession,
    updateActivity,

    // Utility methods
    clearError: () => setAuthError(null),
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};